import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSettings } from '@wu/widget-core';
import { useTables } from '../tables-context';
import { FormPopupContext } from './form-popup-context';
import { FormThemeProvider } from './form-theme-provider';

export function FormPopupProvider({ children }: PropsWithChildren) {
  const { layout, editingItems, editMode = false } = useSettings();

  const editFormColumnId = editMode && editingItems?.['columnWithFormId'];
  const { activeTable } = useTables();
  const { columns = [] } = activeTable ?? {};

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentColumnId, setCurrentColumnId] = useState<string>();

  const currentColumn = useMemo(
    () => columns.find(({ id }) => id === currentColumnId),
    [columns, currentColumnId]
  );

  const openPopup = useCallback((columnId: string) => {
    setIsPopupOpen(true);
    setCurrentColumnId(columnId);
  }, []);
  const closePopup = useCallback(() => {
    setIsPopupOpen(false);
    setCurrentColumnId(undefined);
  }, []);

  useEffect(() => {
    if (typeof editFormColumnId !== 'string') {
      return;
    }

    openPopup(editFormColumnId);
  }, [editFormColumnId, openPopup]);

  useEffect(() => {
    closePopup();
  }, [closePopup, layout]);

  const context = useMemo(
    () => ({
      openPopup,
      closePopup,
      isPopupOpen,
      currentColumn
    }),
    [closePopup, isPopupOpen, openPopup, currentColumn]
  );

  return (
    <FormPopupContext.Provider value={context}>
      <FormThemeProvider>{children}</FormThemeProvider>
    </FormPopupContext.Provider>
  );
}
